import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getReportsSummary, getRecentActivity, getEquipmentByCategory } from "@/lib/api";
import { 
  Monitor, 
  CheckCircle2, 
  ArrowLeftRight, 
  ShoppingCart,
  Users,
  TrendingUp,
  Package,
  RotateCcw,
  DollarSign
} from "lucide-react";

export default function Dashboard() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [summaryRes, activityRes, categoryRes] = await Promise.all([
        getReportsSummary(),
        getRecentActivity(),
        getEquipmentByCategory()
      ]);
      setSummary(summaryRes.data);
      setActivities(activityRes.data);
      setCategoryData(categoryRes.data);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return `${(value || 0).toFixed(3)} TND`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "loan":
        return <ArrowLeftRight className="w-5 h-5" />;
      case "return":
        return <RotateCcw className="w-5 h-5" />;
      case "sale":
        return <DollarSign className="w-5 h-5" />;
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">{t("dashboard.title")}</h1>
        <p className="page-subtitle">{t("dashboard.subtitle")}</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="metric-card" data-testid="metric-total">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.totalEquipment")}</p>
              <p className="metric-card-value">{summary?.total_equipment || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Monitor className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="metric-available">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.available")}</p>
              <p className="metric-card-value text-emerald-600">{summary?.available || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="metric-loaned">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.loaned")}</p>
              <p className="metric-card-value text-amber-600">{summary?.loaned || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="metric-sold">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.sold")}</p>
              <p className="metric-card-value text-slate-600">{summary?.sold || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <Card className="metric-card" data-testid="metric-employees">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.employees")}</p>
              <p className="metric-card-value">{summary?.total_employees || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="metric-value">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.inventoryValue")}</p>
              <p className="metric-card-value text-xl">{formatCurrency(summary?.total_value)}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-violet-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card" data-testid="metric-revenue">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">{t("dashboard.salesRevenue")}</p>
              <p className="metric-card-value text-xl">{formatCurrency(summary?.sales_revenue)}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="border-slate-200" data-testid="recent-activity">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("dashboard.recentActivity")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activities.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t("dashboard.noActivity")}</p>
            ) : (
              <div className="space-y-0">
                {activities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon ${activity.type}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatDate(activity.date)}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500 shrink-0">
                      {activity.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Equipment by Category */}
        <Card className="border-slate-200" data-testid="category-distribution">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("dashboard.equipmentByCategory")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t("dashboard.noData")}</p>
            ) : (
              <div className="space-y-4">
                {categoryData.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">
                          {item.category}
                        </span>
                        <span className="text-sm text-slate-500">
                          {item.count} {t("dashboard.pieces")}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min((item.count / (summary?.total_equipment || 1)) * 100, 100)}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
